import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { PlusIcon, ReorderIcon } from '../../components/ui/icons';
import { useDetailInView } from '../../components/ui/useDetailInView';
import { useAuth } from '../auth/AuthContext';
import type { Focus } from '../layout/focus';
import { useSelectedId } from '../layout/useSelectedId';
import { LocationPanel } from './LocationPanel';
import { LocationTree } from './LocationTree';
import { createLocation, LOCATION_KINDS, type LocationKind } from './api';

export function LocationsPage({ focus }: { focus?: Focus | null }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useSelectedId('/locations');
  const [adding, setAdding] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = can('catalog.write');

  // Opened by scanning the label on the shelf itself — which place is in the
  // address; closing the half-filled "new place" form is what is left to do.
  useEffect(() => {
    if (!focus) return;
    setAdding(false);
  }, [focus]);

  const detail = useDetailInView(String(selectedId));

  const create = useMutation({
    mutationFn: (input: {
      name: string;
      kind: LocationKind;
      parent_id: string | null;
    }) => createLocation(input),
    onSuccess: (created) => {
      setAdding(false);
      setError(null);
      setSelectedId(created.id);
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <div className={`locations${selectedId ? ' is-detail' : ''}`}>
      <aside className="locations__tree">
        <div className="locations__tree-head">
          <h2>{t('nav.locations')}</h2>
          {/* Mark plus word, and the word steps aside when the panel is too
              narrow for it — this column is the one the user drags in to give
              the card beside it more room. The tooltip carries the word either
              way, so nothing is lost by dropping it. */}
          {canEdit && (
            <>
              <button
                type="button"
                className={reordering ? 'is-active' : undefined}
                title={reordering ? t('locations.reorderDone') : t('locations.reorder')}
                onClick={() => setReordering((value) => !value)}
              >
                <ReorderIcon />
                <span className="btn__label">
                  {reordering ? t('locations.reorderDone') : t('locations.reorder')}
                </span>
              </button>
              <button
                type="button"
                title={adding ? t('common.cancel') : t('locations.add')}
                onClick={() => setAdding((value) => !value)}
              >
                <PlusIcon />
                <span className="btn__label">
                  {adding ? t('common.cancel') : t('locations.add')}
                </span>
              </button>
            </>
          )}
        </div>

        {reordering && <p className="locations__hint">{t('locations.reorderHint')}</p>}

        {adding && (
          <form
            className="locations__new"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              create.mutate({
                // No code field: the server allocates one. It exists to be
                // scanned, and inventing unique strings by hand is a decision
                // on every box that buys nothing.
                name: String(form.get('name')),
                kind: String(form.get('kind')) as LocationKind,
                // Empty means a root; otherwise the new node lands inside
                // whatever is currently open, which is what "add" means here.
                parent_id: selectedId,
              });
            }}
          >
            <p className="locations__new-target">
              {selectedId ? t('locations.addingInside') : t('locations.addingRoot')}
            </p>
            <input name="name" placeholder={t('locations.name')} required />
            {/* No preselected type: defaulting to "box" quietly mislabels every
                room and cabinet whose creator left the field alone. */}
            <select name="kind" defaultValue="" required>
              <option value="" disabled>
                {t('locations.kindPlaceholder')}
              </option>
              {LOCATION_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`locations.kind.${kind}`)}
                </option>
              ))}
            </select>
            <button type="submit" disabled={create.isPending}>
              {t('common.save')}
            </button>
            {error && (
              <p className="panel__error">
                {t(`locations.errors.${error}`, t('locations.errors.unknown'))}
              </p>
            )}
          </form>
        )}

        <LocationTree
          selectedId={selectedId}
          reordering={reordering}
          onSelect={setSelectedId}
        />
      </aside>

      <div className="locations__detail" ref={detail}>
        <button type="button" className="pane-back" onClick={() => setSelectedId(null)}>
          ← {t('common.backToList')}
        </button>

        {selectedId ? (
          <LocationPanel
            locationId={selectedId}
            onSelect={setSelectedId}
            onArchived={() => setSelectedId(null)}
          />
        ) : (
          <p className="panel__hint">{t('locations.selectHint')}</p>
        )}
      </div>
    </div>
  );
}
